/**
 * TanStack Query hooks over the accounting-api expense product routes. The DTO types
 * mirror the accounting-api model records (ExpenseReport / ExpenseLine). The per-entity
 * isolation is server-side; these hooks just read/mutate the caller's own data.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';

export type ReportStatus = 'OPEN' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export interface ExpenseReport {
  reportId: number;
  entityId: number;
  bookId: number;
  ownerUserOid: string;
  periodCode: string;
  title: string | null;
  status: ReportStatus;
  submittedAt: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseLine {
  lineId: number;
  reportId: number;
  lineType: 'CARD_CHARGE' | 'OUT_OF_POCKET' | 'MILEAGE';
  amount: string;
  currency: string;
  txnDate: string;
  vendor: string | null;
  description: string | null;
  glAccountId: number | null;
  attributeValuesJson: string;
  postingJournalEntryId: number | null;
}

export interface ReportDetail {
  report: ExpenseReport;
  lines: ExpenseLine[];
  total: string;
}

const keys = {
  list: (status?: string) =>
    ['expense', 'reports', { status: status ?? 'all' }] as const,
  detail: (id: number) => ['expense', 'reports', id] as const,
};

/** List the caller's reports, optionally filtered by status. */
export function useReports(status?: ReportStatus) {
  return useQuery({
    queryKey: keys.list(status),
    queryFn: () =>
      expenseFetch<ExpenseReport[]>(
        `/v1/expense/reports${status ? `?status=${status}` : ''}`,
      ),
  });
}

/** A report with its lines + total. */
export function useReportDetail(reportId: number | null) {
  return useQuery({
    queryKey: reportId ? keys.detail(reportId) : ['expense', 'reports', 'none'],
    enabled: reportId != null,
    queryFn: () =>
      expenseFetch<ReportDetail>(`/v1/expense/reports/${reportId}`),
  });
}

/** Create a new monthly report. */
export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { period: string; title?: string }) =>
      expenseFetch<ExpenseReport>('/v1/expense/reports', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense', 'reports'] }),
  });
}

/** Submit a report for approval (422 with violations if it fails the gate). */
export function useSubmitReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: number) =>
      expenseFetch<ExpenseReport>(`/v1/expense/reports/${reportId}/submit`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense', 'reports'] }),
  });
}

/** The request body the API's AddLineRequest accepts (mileage fields optional). */
export interface NewLineRequest {
  lineType: ExpenseLine['lineType'];
  amount?: string;
  currency?: string;
  txnDate: string;
  vendor?: string;
  description?: string;
  glAccountId?: number | null;
  attributeValuesJson?: string;
  mileageMiles?: string;
  mileageRateEffective?: string;
}

/** Add a line to an OPEN report. Invalidates the report detail so the table + total refresh. */
export function useAddLine(reportId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (line: NewLineRequest) =>
      expenseFetch<ExpenseLine>(`/v1/expense/reports/${reportId}/lines`, {
        method: 'POST',
        body: JSON.stringify(line),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(reportId) });
      qc.invalidateQueries({ queryKey: ['expense', 'reports'] });
    },
  });
}

/** Delete a line from an OPEN report. */
export function useDeleteLine(reportId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) =>
      expenseFetch<void>(`/v1/expense/reports/${reportId}/lines/${lineId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(reportId) });
      qc.invalidateQueries({ queryKey: ['expense', 'reports'] });
    },
  });
}
